import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronRight, Home, Shield, FileCheck, Clock, DollarSign, CheckCircle2, Download, AlertCircle } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
const licenses = [{
  title: 'Agrochemical Dealer License',
  authority: 'Agricultural Chemicals Board',
  color: 'bg-red-600',
  validity: '1 Year',
  fee: 'UGX 500,000',
  processing: '14-21 working days',
  description: 'Required for all businesses involved in the importation, distribution, sale, or storage of agricultural chemicals including pesticides, herbicides, and fungicides in Uganda.',
  requirements: ['Completed application form (ACB Form 1)', 'Certificate of registration of business', 'Proof of suitable storage facilities', 'Qualified personnel (certificate in agriculture or related field)', 'Tax Identification Number (TIN)', 'Inspection report of premises']
}, {
  title: 'Seed Dealer License',
  authority: 'National Seed Certification Service (NSCS)',
  color: 'bg-green-600',
  validity: '1 Year',
  fee: 'UGX 300,000',
  processing: '10-14 working days',
  description: 'Mandatory for businesses engaged in the production, processing, marketing, importation, or exportation of seed in Uganda under the Seeds and Plant Act.',
  requirements: ['Application letter to Commissioner Crop Resources', 'Business registration certificate', 'Proof of adequate seed storage facilities', 'Qualified seed analyst or agronomist on staff', 'Previous seed business records (for renewals)', 'Inspection of storage and processing facilities']
}, {
  title: 'Veterinary Drug Dealer License',
  authority: 'National Drug Authority / MAAIF',
  color: 'bg-blue-600',
  validity: '1 Year',
  fee: 'UGX 400,000',
  processing: '14-21 working days',
  description: 'Required for pharmacies, agro-vet shops, and businesses dealing in veterinary drugs, vaccines, and biologicals for animal health in Uganda.',
  requirements: ['Application to Commissioner Animal Health', 'Pharmacy or agro-vet shop registration', 'Qualified veterinary pharmacist or animal health worker', 'Suitable drug storage conditions (cold chain for vaccines)', 'NDA registration of products to be stocked', 'Premises inspection certificate']
}, {
  title: 'Fish Processing & Export License',
  authority: 'Directorate of Fisheries Resources',
  color: 'bg-cyan-600',
  validity: '1 Year',
  fee: 'UGX 1,000,000',
  processing: '21-30 working days',
  description: 'Mandatory for fish processing establishments intending to process and export fish and fishery products, particularly to EU and international markets.',
  requirements: ['Application to Commissioner for Fisheries', 'HACCP plan and implementation evidence', 'Factory layout and processing flow diagrams', 'Water quality test results', 'Staff health certificates', 'Environmental Impact Assessment approval', 'Uganda National Bureau of Standards (UNBS) certification']
}, {
  title: 'Fishing License',
  authority: 'Directorate of Fisheries Resources',
  color: 'bg-sky-600',
  validity: '1 Year',
  fee: 'UGX 50,000 - 500,000 (varies by vessel)',
  processing: '5-7 working days',
  description: "Required for all persons and vessels engaged in commercial fishing activities on Uganda's lakes, rivers, and other water bodies.",
  requirements: ['Application form from local Beach Management Unit (BMU)', 'National ID or passport', 'Vessel registration certificate', 'Approved fishing gear inspection', 'BMU membership confirmation', 'Payment of applicable fees']
}, {
  title: 'Dairy Processing License',
  authority: 'Dairy Development Authority (DDA)',
  color: 'bg-purple-600',
  validity: '1 Year',
  fee: 'UGX 500,000 - 2,000,000 (by scale)',
  processing: '21-30 working days',
  description: 'Required for all dairy processing plants, mini-dairies, and milk collection centers operating in Uganda under the Dairy Industry Act.',
  requirements: ['Application to DDA Managing Director', 'Factory layout and equipment specifications', 'HACCP or food safety management plan', 'Water quality and effluent management plan', 'Qualified dairy technologist on staff', 'UNBS quality mark application']
}];
export function LicensingPage() {
  return <>
      <Header data-id="element-960" />
      <main className="flex-grow pt-[160px] md:pt-[148px] bg-gray-50 pb-20" data-id="element-961">
        <div className="relative text-white py-32 md:py-48 overflow-hidden" data-id="element-962">
          <div className="absolute inset-0 bg-cover bg-center" style={{
          backgroundImage: 'url("https://images.unsplash.com/photo-1516467508483-a7212febe31a?w=1600&h=600&fit=crop")'
        }} data-id="element-963"></div>
          <div className="absolute inset-0 bg-uganda-green/80" data-id="element-964"></div>
          <div className="relative max-w-7xl mx-auto px-4 md:px-8" data-id="element-965">
            <div className="flex items-center text-sm text-green-100 mb-6" data-id="element-966">
              <Link to="/" className="hover:text-white flex items-center" data-id="element-967">
                <Home className="w-4 h-4 mr-1" data-id="element-968" /> Home
              </Link>
              <ChevronRight className="w-4 h-4 mx-2" data-id="element-969" />
              <span data-id="element-970">Services & Applications</span>
              <ChevronRight className="w-4 h-4 mx-2" data-id="element-971" />
              <span className="text-white font-medium" data-id="element-972">Licensing</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4" data-id="element-973">Licensing</h1>
            <p className="text-lg text-green-50 max-w-3xl leading-relaxed" data-id="element-974">
              Information on licenses and permits required for agricultural
              businesses, dealers, processors, and service providers operating
              in Uganda.
            </p>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-8 mt-12" data-id="element-975">
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 mb-10 flex items-start gap-3" data-id="element-976">
            <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" data-id="element-977" />
            <p className="text-sm text-amber-800" data-id="element-978">
              All licenses must be renewed annually. Operating without a valid
              license is an offence under the respective Acts of Parliament and
              may result in fines, closure, or prosecution.
            </p>
          </div>

          <div className="space-y-6" data-id="element-979">
            {licenses.map((license, index) => <motion.div key={license.title} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: index * 0.08
          }} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-id="element-980">
                <div className="flex flex-col lg:flex-row" data-id="element-981">
                  <div className="lg:w-2/3 p-6 md:p-8" data-id="element-982">
                    <div className="flex items-center gap-4 mb-4" data-id="element-983">
                      <div className={`w-12 h-12 rounded-xl ${license.color} flex items-center justify-center`} data-id="element-984">
                        <Shield className="w-6 h-6 text-white" data-id="element-985" />
                      </div>
                      <div data-id="element-986">
                        <h3 className="text-xl font-bold text-uganda-black" data-id="element-987">
                          {license.title}
                        </h3>
                        <p className="text-sm text-gray-500" data-id="element-988">
                          Issued by: {license.authority}
                        </p>
                      </div>
                    </div>
                    <p className="text-gray-600 leading-relaxed mb-6" data-id="element-989">
                      {license.description}
                    </p>
                    <h4 className="text-sm font-bold text-uganda-black mb-3 uppercase tracking-wider" data-id="element-990">
                      Requirements
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2" data-id="element-991">
                      {license.requirements.map((req, i) => <div key={i} className="flex items-start gap-2" data-id="element-992">
                          <CheckCircle2 className="w-4 h-4 text-uganda-green flex-shrink-0 mt-0.5" data-id="element-993" />
                          <span className="text-sm text-gray-600" data-id="element-994">{req}</span>
                        </div>)}
                    </div>
                  </div>
                  <div className="lg:w-1/3 bg-gray-50 p-6 md:p-8 border-t lg:border-t-0 lg:border-l border-gray-200 flex flex-col justify-between" data-id="element-995">
                    <div className="space-y-4 mb-6" data-id="element-996">
                      <div className="flex items-center gap-3" data-id="element-997">
                        <Clock className="w-5 h-5 text-gray-400" data-id="element-998" />
                        <div data-id="element-999">
                          <p className="text-xs text-gray-500 font-medium uppercase tracking-wider" data-id="element-1000">
                            Validity
                          </p>
                          <p className="font-bold text-uganda-black" data-id="element-1001">
                            {license.validity}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3" data-id="element-1002">
                        <DollarSign className="w-5 h-5 text-gray-400" data-id="element-1003" />
                        <div data-id="element-1004">
                          <p className="text-xs text-gray-500 font-medium uppercase tracking-wider" data-id="element-1005">
                            Fee
                          </p>
                          <p className="font-bold text-uganda-black" data-id="element-1006">
                            {license.fee}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3" data-id="element-1007">
                        <FileCheck className="w-5 h-5 text-gray-400" data-id="element-1008" />
                        <div data-id="element-1009">
                          <p className="text-xs text-gray-500 font-medium uppercase tracking-wider" data-id="element-1010">
                            Processing Time
                          </p>
                          <p className="font-bold text-uganda-black" data-id="element-1011">
                            {license.processing}
                          </p>
                        </div>
                      </div>
                    </div>
                    <button className="w-full flex items-center justify-center gap-2 bg-uganda-green text-white py-3 rounded-lg font-semibold hover:bg-green-800 transition-colors" data-id="element-1012">
                      <Download className="w-4 h-4" data-id="element-1013" /> Download Form
                    </button>
                  </div>
                </div>
              </motion.div>)}
          </div>
        </div>
      </main>
      <Footer data-id="element-1014" />
    </>;
}